import { useEffect, useMemo, useState } from "react"

import algoliasearch from "algoliasearch/lite"
import { graphql, useStaticQuery } from "gatsby"

const GATSBY_ALGOLIA_API_KEY = process.env.GATSBY_ALGOLIA_API_KEY ?? ""
const GATSBY_ALGOLIA_APPLICATION_ID =
  process.env.GATSBY_ALGOLIA_APPLICATION_ID ?? ""
const GATSBY_ALGOLIA_INDEX = process.env.GATSBY_ALGOLIA_INDEX ?? ""

/*To add facet :
    1. Add to "Facet" type
    2. Add to "facets" array
    3. Repeat step 1 and 2 in AlgoliaFacets.ts of plugin "gatsby-source-chefclub'
 */
type Facet =
  | "vertical"
  | "difficulty"
  | "suitable_for_diets.slug"
  | "recipe_type"
const facets: Facet[] = [
  "vertical",
  "difficulty",
  "suitable_for_diets.slug",
  "recipe_type",
]

type InitialFacets = {
  [key in Facet]: any
}
const emptyInitialFacets: InitialFacets = {
  vertical: [],
  "suitable_for_diets.slug": [],
  difficulty: [],
  recipe_type: [],
}

export default function useAlgoliaFacets() {
  const baseInitialFacetsData: any[] = useStaticQuery(graphql`
    query AlgoliaFacetsQuery {
      allAlgoliaFacets {
        nodes {
          json
          facetName
        }
      }
    }
  `).allAlgoliaFacets.nodes
  const baseInitialFacets: InitialFacets = emptyInitialFacets
  Object.keys(baseInitialFacets).map((key: string) => {
    baseInitialFacets[key as Facet] = JSON.parse(
      baseInitialFacetsData.filter(
        (facetData: any) => facetData.facetName === key
      )[0].json
    )
  })

  const [isError, setError] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const searchClient = useMemo(
    () => algoliasearch(GATSBY_ALGOLIA_APPLICATION_ID, GATSBY_ALGOLIA_API_KEY),
    []
  )
  const [initialFacets, setInitialFacets] = useState<InitialFacets>({
    ...baseInitialFacets,
  })

  const refetch = async () => {
    setIsLoading(true)

    const tempInitialFacets: InitialFacets = { ...emptyInitialFacets }

    try {
      await Promise.all(
        facets.map(async facet => {
          await searchClient
            .searchForFacetValues([
              {
                indexName: GATSBY_ALGOLIA_INDEX,
                params: {
                  facetName: facet,
                  facetQuery: "",
                },
              },
            ])
            .then(([{ facetHits }]) => {
              tempInitialFacets[facet] = []
              tempInitialFacets[facet].push(
                ...facetHits.map(facet => ({
                  ...facet,
                  label: facet.value,
                  value: facet.value,
                  isRefined: false,
                  count: 0,
                }))
              )
            })
        })
      )
      setInitialFacets(tempInitialFacets)
      setIsSuccess(true)
      setError(false)
    } catch (e) {
      setIsSuccess(false)
      setError(true)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    refetch()
  }, [])

  return { isSuccess, isLoading, isError, initialFacets, refetch }
}

const mergeItems = ({
  activeItems,
  inactiveItems,
  compareKey = "value",
}: {
  activeItems: any[]
  inactiveItems: any[]
  compareKey?: string
}) => {
  const activeItemsValues = activeItems.map((i: any) => i[compareKey]).flat()

  return [
    ...activeItems, //Active value
    ...inactiveItems.filter(
      (item: any) => !activeItemsValues.includes(item[compareKey])
    ),
  ]
}

export { mergeItems }
